import { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import config from "../config.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsMain = ({ data = [] }) => {
  const [GoogleData, setGoogleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    // If data was passed as prop, use it
    if (data && data.length > 0) {
      setGoogleData(data);
      setLoading(false);
      return;
    }

    // Otherwise fetch data
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/MarketingworkbookToJson`);
        // Make sure we're accessing the correct path in the response
        const googleData = response.data.data?.Google || [];
        setGoogleData(googleData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error);
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL, data]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography variant="h6" color="error">Error fetching data</Typography>;
  }

  // Check if we have data
  if (!GoogleData || GoogleData.length === 0) {
    return <Typography variant="h6">No data available</Typography>;
  }

  // Safe mapping with fallbacks
  const dates = GoogleData.map(entry => entry.DATE || '');
  const directData = GoogleData.map(entry => entry.DIRECT || 0);
  const organicData = GoogleData.map(entry => entry.ORGANIC || 0);
  const referralData = GoogleData.map(entry => entry.REFERRAL || 0);
  const paidData = GoogleData.map(entry => entry.PAID || 0);
  const otherData = GoogleData.map(entry => entry.OTHER || 0);

  const chartData = {
    labels: dates,
    datasets: [
      {
        label: 'Direct',
        data: directData,
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
      },
      {
        label: 'Organic',
        data: organicData,
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
      },
      {
        label: 'Referral',
        data: referralData,
        borderColor: 'rgba(255, 206, 86, 1)',
        backgroundColor: 'rgba(255, 206, 86, 0.2)',
      },
      {
        label: 'Paid',
        data: paidData,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Other',
        data: otherData,
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: true,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date',
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Subscribers',
        },
      },
    },
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="textPrimary">
          Google Analytics
        </Typography>
        <Button variant="contained" color="primary">
          Main source last 30 days
        </Button>
      </Box>
      <Box>
        <List sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <ListItem sx={{ color: 'textSecondary' }}>Direct</ListItem>
          <ListItem sx={{ color: 'textSecondary' }}>Organic</ListItem>
          <ListItem sx={{ color: 'textSecondary' }}>Referral</ListItem>
          <ListItem sx={{ color: 'textSecondary' }}>Paid</ListItem>
        </List>
      </Box>
      <Line data={chartData} options={options} />
    </Box>
  );
};

export default AnalyticsMain;
